// dishEconomics.ts
// Fonte UNICA della matematica prezzo↔costo del food cost. Scorpora l'IVA dal
// prezzo di vendita (che a menù è LORDO) così food cost % e margine sono
// coerenti col costo alimento (NETTO imponibile). Sostituisce la formula
// duplicata in FoodCostPage / foodCostExports / grafici report.
export interface DishEconomicsInput {
  costPerPortion: number | null;
  sellingPriceCentsGross: number | null; // prezzo menù, IVA inclusa
  vatRatePct: number;                     // override ricetta ?? default locale
}
export interface DishEconomics {
  sellingGross: number | null;
  sellingNet: number | null;
  vatRatePct: number;
  foodCostPct: number | null;
  marginNet: number | null;
}

/**
 * Decisione di prezzo per un ristorante, basata esclusivamente sul costo
 * materia prima della porzione. Non rappresenta utile, costo pieno, personale
 * o costi fissi: quei dati non sono disponibili in questo contratto.
 *
 * Tutti i campi decisionali sono null quando anche un solo input necessario non
 * e' valido. Cosi' un chiamante non puo' trasformare un costo mancante in uno
 * zero implicito.
 */
export interface RestaurantDecisionInput extends DishEconomicsInput {
  /** Food cost % desiderato, ad esempio 30 per il 30%. */
  targetFoodCostPct: number | null;
  /** Aumento ipotetico del costo materia prima, percentuale non negativa. */
  costStressPct?: number | null;
}

export interface RestaurantDecisionEconomics {
  status: 'ready' | 'incomplete';
  /** Prezzo e margine correnti, sempre al netto IVA quando calcolabili. */
  current: DishEconomics;
  /** Margine materie / prezzo netto. Non e' un utile netto. */
  materialMarginPct: number | null;
  targetNetPrice: number | null;
  targetGrossPrice: number | null;
  /** Prezzo lordo target meno listino lordo corrente. */
  listPriceDeltaGross: number | null;
  stressedCostPerPortion: number | null;
  stressedFoodCostPct: number | null;
  stressedMaterialMarginNet: number | null;
  stressedMaterialMarginPct: number | null;
  stressedTargetNetPrice: number | null;
  stressedTargetGrossPrice: number | null;
  stressedListPriceDeltaGross: number | null;
}

function emptyRestaurantDecision(current: DishEconomics): RestaurantDecisionEconomics {
  return {
    status: 'incomplete', current, materialMarginPct: null,
    targetNetPrice: null, targetGrossPrice: null, listPriceDeltaGross: null,
    stressedCostPerPortion: null, stressedFoodCostPct: null,
    stressedMaterialMarginNet: null, stressedMaterialMarginPct: null,
    stressedTargetNetPrice: null, stressedTargetGrossPrice: null, stressedListPriceDeltaGross: null,
  };
}

/**
 * Calcola prezzo target, delta listino e uno scenario di inflazione del costo.
 * Formula target: costo porzione / (target food cost / 100), poi riconversione
 * al lordo con la stessa IVA del piatto. E' pura e non arrotonda: la UI decide
 * soltanto come mostrare i centesimi.
 */
export function computeRestaurantDecision(i: RestaurantDecisionInput): RestaurantDecisionEconomics {
  const current = computeDishEconomics(i);
  const stress = i.costStressPct ?? 0;
  const valid = i.costPerPortion != null && Number.isFinite(i.costPerPortion) && i.costPerPortion >= 0
    && i.sellingPriceCentsGross != null && Number.isFinite(i.sellingPriceCentsGross) && i.sellingPriceCentsGross > 0
    && Number.isFinite(i.vatRatePct) && i.vatRatePct >= 0
    && i.targetFoodCostPct != null && Number.isFinite(i.targetFoodCostPct) && i.targetFoodCostPct > 0 && i.targetFoodCostPct <= 100
    && Number.isFinite(stress) && stress >= 0;
  if (!valid || current.sellingNet == null || current.sellingGross == null || current.marginNet == null) {
    return emptyRestaurantDecision(current);
  }

  // `valid` above has established these as finite values; aliases keep that
  // fail-closed boundary explicit to TypeScript and to future readers.
  const cost = i.costPerPortion as number;
  const targetFoodCostPct = i.targetFoodCostPct as number;
  const vatMultiplier = 1 + i.vatRatePct / 100;
  const targetNetPrice = cost / (targetFoodCostPct / 100);
  const targetGrossPrice = targetNetPrice * vatMultiplier;
  const stressedCostPerPortion = cost * (1 + stress / 100);
  const stressed = computeDishEconomics({
    costPerPortion: stressedCostPerPortion,
    sellingPriceCentsGross: i.sellingPriceCentsGross,
    vatRatePct: i.vatRatePct,
  });
  const stressedTargetNetPrice = stressedCostPerPortion / (targetFoodCostPct / 100);
  const stressedTargetGrossPrice = stressedTargetNetPrice * vatMultiplier;
  return {
    status: 'ready', current,
    materialMarginPct: current.marginNet / current.sellingNet * 100,
    targetNetPrice, targetGrossPrice,
    listPriceDeltaGross: targetGrossPrice - current.sellingGross,
    stressedCostPerPortion,
    stressedFoodCostPct: stressed.foodCostPct,
    stressedMaterialMarginNet: stressed.marginNet,
    stressedMaterialMarginPct: stressed.marginNet != null ? stressed.marginNet / current.sellingNet * 100 : null,
    stressedTargetNetPrice, stressedTargetGrossPrice,
    stressedListPriceDeltaGross: stressedTargetGrossPrice - current.sellingGross,
  };
}

export function computeDishEconomics(i: DishEconomicsInput): DishEconomics {
  const vat = Number.isFinite(i.vatRatePct) && i.vatRatePct > 0 ? i.vatRatePct : 0;
  const gross = i.sellingPriceCentsGross != null && Number.isFinite(i.sellingPriceCentsGross) && i.sellingPriceCentsGross >= 0 ? i.sellingPriceCentsGross / 100 : null;
  const net = gross != null && Number.isFinite(i.vatRatePct) && i.vatRatePct >= 0 ? gross / (1 + vat / 100) : null;
  const cost = i.costPerPortion;
  const usable = cost != null && Number.isFinite(cost) && cost >= 0 && net != null && net > 0;
  return {
    sellingGross: gross,
    sellingNet: net,
    vatRatePct: vat,
    foodCostPct: usable ? (cost / net) * 100 : null,
    marginNet: usable ? net - cost : null,
  };
}
